import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ScanSearch, ArrowRight, Loader2, AlertTriangle, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, getScanIds } from "@/lib/api";
import { scoreColor, scoreBand } from "@/lib/constants";

export default function Dashboard() {
  const nav = useNavigate();
  const [scans, setScans] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const ids = getScanIds();
      if (!ids.length) { setLoading(false); return; }
      try {
        const [b, h] = await Promise.all([
          api.post("/scans/batch", { ids }),
          api.post("/scans/history", { ids }),
        ]);
        setScans(b.data.scans || []);
        setHistory(h.data.businesses || []);
      } catch (e) { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const completed = scans.filter((s) => s.status === "complete");
  const avg = completed.length ? Math.round(completed.reduce((a, s) => a + (s.health_score || 0), 0) / completed.length) : null;
  const totalOpps = completed.reduce((a, s) => a + (s.opportunity_count || 0), 0);
  const totalHigh = completed.reduce((a, s) => a + (s.high_priority || 0), 0);

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Your Dashboard</h1>
            <p className="mt-2 text-slate-600">Track your scans, health scores and discovered opportunities.</p>
          </div>
          <Button onClick={() => nav("/scan")} data-testid="dashboard-new-scan"
            className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 active:scale-95 transition-transform">
            <Plus className="w-4 h-4 mr-1.5" /> Run New Scan
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <SummaryCard value={scans.length} label="Total scans" />
          <SummaryCard value={avg ?? "–"} label="Avg. health score" color={avg != null ? scoreColor(avg) : undefined} />
          <SummaryCard value={totalOpps} label="Opportunities found" />
          <SummaryCard value={totalHigh} label="High-priority issues" color="#dc2626" />
        </div>

        {history.filter((g) => g.scan_count >= 2).length > 0 && (
          <BusinessHistory groups={history.filter((g) => g.scan_count >= 2)} onOpen={(id) => nav(`/report/${id}`)} />
        )}

        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="w-7 h-7 text-indigo-600 animate-spin" /></div>
        ) : scans.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-14 text-center">
            <span className="grid place-items-center w-14 h-14 rounded-xl bg-slate-900 text-white mx-auto mb-5"><ScanSearch className="w-7 h-7" /></span>
            <h3 className="font-heading text-xl font-semibold text-slate-900">No scans yet</h3>
            <p className="mt-2 text-slate-500 max-w-sm mx-auto">Run your first scan to uncover the opportunities your business may be missing.</p>
            <Button onClick={() => nav("/scan")} data-testid="empty-start-scan"
              className="mt-6 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 active:scale-95 transition-transform">
              Start Your First Scan <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {scans.map((s, i) => (
              <motion.button key={s.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }} onClick={() => nav(`/report/${s.id}`)}
                data-testid={`scan-card-${i}`}
                className="text-left bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-slate-900 truncate">{s.business?.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">{s.business?.category} · {s.business?.city}</p>
                  </div>
                  {s.status === "complete" ? (
                    <div className="text-right shrink-0 ml-3">
                      <div className="font-heading text-2xl font-bold leading-none" style={{ color: scoreColor(s.health_score) }}>{s.health_score}</div>
                      <div className="text-[10px] text-slate-400">{scoreBand(s.health_score)}</div>
                    </div>
                  ) : s.status === "failed" ? (
                    <AlertTriangle className="w-5 h-5 text-orange-500 shrink-0" />
                  ) : (
                    <Loader2 className="w-5 h-5 text-indigo-500 animate-spin shrink-0" />
                  )}
                </div>
                <div className="mt-5 flex items-center gap-4 text-xs">
                  <span className="text-slate-600"><b className="text-slate-900">{s.opportunity_count ?? 0}</b> opportunities</span>
                  <span className="text-red-600"><b>{s.high_priority ?? 0}</b> high-priority</span>
                </div>
                <div className="mt-3 text-[11px] text-slate-400">{new Date(s.created_at).toLocaleString()}</div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ value, label, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <div className="font-heading text-3xl font-bold leading-none" style={color ? { color } : { color: "#0f172a" }}>{value}</div>
      <div className="text-xs text-slate-500 mt-2">{label}</div>
    </div>
  );
}

function BusinessHistory({ groups, onOpen }) {
  return (
    <div className="mb-10" data-testid="business-history">
      <h2 className="font-heading text-xl font-semibold text-slate-900 mb-1">Business history</h2>
      <p className="text-sm text-slate-500 mb-5">How each business's Business Health Score has changed over time.</p>
      <div className="grid md:grid-cols-2 gap-5">
        {groups.map((g, i) => {
          const scores = g.series.map((s) => s.health_score ?? 0);
          const change = g.change;
          return (
            <div key={i} data-testid={`history-card-${i}`} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-slate-900 truncate">{g.business?.name}</p>
                  <p className="text-xs text-slate-500">{g.scan_count} scans · {g.business?.category}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading text-2xl font-bold leading-none" style={{ color: scoreColor(g.latest_score) }}>{g.latest_score}</div>
                  {change && <DeltaBadge delta={change.score_delta} />}
                </div>
              </div>
              <div className="mt-4"><Sparkline scores={scores} /></div>
              {change && (change.resolved_opportunities.length > 0 || change.new_opportunities.length > 0) && (
                <div className="mt-4 flex flex-wrap gap-2 text-xs">
                  {change.resolved_opportunities.length > 0 && (
                    <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2.5 py-1 font-medium">
                      {change.resolved_opportunities.length} resolved since last scan
                    </span>
                  )}
                  {change.new_opportunities.length > 0 && (
                    <span className="rounded-full bg-red-50 border border-red-200 text-red-700 px-2.5 py-1 font-medium">
                      {change.new_opportunities.length} new
                    </span>
                  )}
                </div>
              )}
              <button onClick={() => onOpen(g.latest_id)} data-testid={`history-open-${i}`}
                className="mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1">
                View latest report <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Sparkline({ scores }) {
  if (!scores.length) return null;
  const w = 240, h = 44;
  const step = scores.length > 1 ? w / (scores.length - 1) : w;
  const pts = scores.map((s, i) => `${i * step},${h - (s / 100) * h}`).join(" ");
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-11">
      <polyline points={pts} fill="none" stroke="#4f46e5" strokeWidth="2" vectorEffect="non-scaling-stroke" />
      {scores.map((s, i) => <circle key={i} cx={i * step} cy={h - (s / 100) * h} r="3" fill="#4f46e5" />)}
    </svg>
  );
}

function DeltaBadge({ delta }) {
  const flat = delta === 0;
  const up = delta > 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const cls = flat ? "text-slate-500" : up ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${cls}`}>
      <Icon className="w-3.5 h-3.5" />{up ? `+${delta}` : delta} pts
    </span>
  );
}
